import type { SolverSpeed } from '../../stores/SolverStore';

/**
 * How long a "balanced" (Guter Plan) run takes, bucketed by the number of cards it places.
 * The backend stops a balanced run once the plan stops improving (2 min without improvement,
 * 10 min while hard violations remain, hard cap 20 min, see `SOLVER_TIMING`) — larger plans take
 * longer to settle.
 */
export type BalancedDurationBucket = 'small' | 'medium' | 'large';

/** Upper bound (exclusive) of the "small" bucket. */
export const BALANCED_SMALL_MAX_CARDS = 300;
/** Upper bound (inclusive) of the "medium" bucket. */
export const BALANCED_MEDIUM_MAX_CARDS = 700;

/** N < 300 → small (~2–5 min), 300–700 → medium (~5–10 min), > 700 → large (~10–20 min). */
export function balancedDurationBucket(cardCount: number): BalancedDurationBucket {
  if (cardCount < BALANCED_SMALL_MAX_CARDS) {
    return 'small';
  }
  if (cardCount <= BALANCED_MEDIUM_MAX_CARDS) {
    return 'medium';
  }
  return 'large';
}

/**
 * When the cp-sat solver ends a run by itself. Mirror of cp-sat-solver
 * `src/cpsat_timetabling/planning/solve.py` (`_NORMAL_IDLE_SECONDS`, `_HEURISTIC_DRAFT_SECONDS`,
 * `_MODE_BUDGET`) — keep in sync when the solver changes. A run that ends by itself is imported
 * automatically by the backend's solver monitor.
 */
export const SOLVER_TIMING = {
  /** fast / DRAFT: `_HEURISTIC_DRAFT_SECONDS` – at most this long (earlier when nothing improves for 4 s). */
  draftMaxMs: 30_000,
  /** balanced / NORMAL: `_NORMAL_IDLE_SECONDS` – ends once no better plan came for this long. */
  normalIdleMs: 120_000,
  /** balanced / NORMAL: `_MODE_BUDGET["NORMAL"]` – hard cap after the start. */
  normalMaxMs: 20 * 60_000,
  // thorough / COMPLEX: does not end early, runs until its long budget – no countdown.
} as const;

/**
 * Countdown until a run ends (and its plan is applied) by itself.
 * `idle`: ends when nothing improves any more; `deadline`: ends at the latest after `remainingMs`.
 * `remainingMs` is never negative (0 = "any moment now").
 */
export type SolverCountdown = { kind: 'idle' | 'deadline'; remainingMs: number };

/**
 * Remaining time until the solver ends a run by itself, or null when it can't be told (thorough,
 * unknown start). `sinceImprovedMs` is the time since the last better plan (null while none exists);
 * it only refreshes with the backend poll (~10 s), so the idle countdown may jump back up. The rare
 * 10-minute idle limit while hard conflicts remain is not visible to the frontend and is ignored.
 */
export function solverCountdown(
  speed: SolverSpeed,
  elapsedMs: number | null,
  sinceImprovedMs: number | null,
): SolverCountdown | null {
  if (elapsedMs == null) {
    return null;
  }
  switch (speed) {
    case 'fast':
      return { kind: 'deadline', remainingMs: Math.max(0, SOLVER_TIMING.draftMaxMs - elapsedMs) };
    case 'balanced': {
      const untilCap = SOLVER_TIMING.normalMaxMs - elapsedMs;
      const untilIdle = sinceImprovedMs == null ? null : SOLVER_TIMING.normalIdleMs - sinceImprovedMs;
      // The idle clock only runs once a plan exists; otherwise (or if the cap comes first) the cap ends the run.
      return untilIdle != null && untilIdle < untilCap
        ? { kind: 'idle', remainingMs: Math.max(0, untilIdle) }
        : { kind: 'deadline', remainingMs: Math.max(0, untilCap) };
    }
    default:
      return null;
  }
}
